import { create } from 'zustand';

type NotificationType = 'error' | 'warning' | 'info' | 'success';

/**
 * A button on the toast: a `handler` to run, or a `to` route to open (the toast
 * sits outside every page, so a page cannot hand it a navigate function).
 */
export interface NotificationAction {
  label: string;
  handler?: () => void;
  to?: string;
}

interface Notification {
  id: string;
  message: string;
  type: NotificationType;
  timestamp: number;
  action?: NotificationAction;
  /** Auto-hide delay in ms. Pass `0` (or any non-positive value)
   *  to make the toast persistent — it stays until `dismissNotification`
   *  is called explicitly (or the user clicks the close X). */
  duration?: number;
  /** Show an inline spinner next to the message. Used by long-running
   *  on-demand jobs (e.g. report generation) to indicate "in progress"
   *  visually. */
  showSpinner?: boolean;
}

export interface NotificationInput {
  message: string;
  type: NotificationType;
  action?: NotificationAction;
  duration?: number;
  showSpinner?: boolean;
}

/**
 * The most recent message handed to `addNotification`, whether or not it became
 * a visible toast.
 *
 * The dedup below suppresses an identical message within 5 seconds, which is
 * right for the toast — the earlier one is still on screen. It is wrong for a
 * screen reader, where the announcement is transient and there is nothing left
 * to perceive. A dietitian who corrects the wrong field and saves again inside
 * 5 seconds would otherwise get total silence, which reads as success.
 *
 * `seq` increments on every call, so an identical repeat is still an
 * observable change for subscribers.
 */
export interface AnnouncementSignal {
  message: string;
  type: NotificationType;
  seq: number;
}

interface NotificationState {
  notifications: Notification[];
  /** Latest message for assistive tech, set even when the toast is deduped. */
  lastAnnouncement: AnnouncementSignal | null;
  /** Returns the id of the created notification so callers that
   *  need to dismiss it later (e.g. persistent progress toasts) can
   *  hold onto it. Returns `null` when the input was deduplicated. */
  addNotification: (
    messageOrObj: string | NotificationInput,
    type?: NotificationType,
  ) => string | null;
  dismissNotification: (id: string) => void;
  clearAll: () => void;
}

export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],
  lastAnnouncement: null,

  addNotification: (messageOrObj, type) => {
    // Support both: addNotification('msg', 'error') and addNotification({ message, type, action })
    const isString = typeof messageOrObj === 'string';
    const msg = isString ? messageOrObj : messageOrObj.message;
    const notifType = isString ? (type ?? 'info') : messageOrObj.type;
    const action = isString ? undefined : messageOrObj.action;
    const duration = isString ? undefined : messageOrObj.duration;
    const showSpinner = isString ? undefined : messageOrObj.showSpinner;

    const now = Date.now();
    let createdId: string | null = null;

    // Signal BEFORE the dedup decision, so a suppressed duplicate is still
    // spoken to a screen reader. See AnnouncementSignal above.
    set((prev) => ({
      lastAnnouncement: {
        message: msg,
        type: notifType,
        seq: (prev.lastAnnouncement?.seq ?? 0) + 1,
      },
    }));

    set((prev) => {
      // Deduplicate: skip if same message exists within the last 5 seconds
      const isDuplicate = prev.notifications.some(
        (n) => n.message === msg && now - n.timestamp < 5000,
      );
      if (isDuplicate) return prev;

      createdId = `${now}-${Math.random().toString(36).slice(2, 8)}`;
      return {
        notifications: [
          ...prev.notifications,
          { id: createdId, message: msg, type: notifType, timestamp: now, action, duration, showSpinner },
        ],
      };
    });

    return createdId;
  },

  dismissNotification: (id) =>
    set((prev) => ({
      notifications: prev.notifications.filter((n) => n.id !== id),
    })),

  clearAll: () => set({ notifications: [] }),
}));
